import React, { useCallback, useMemo } from 'react'
import { enableMapSet, produce } from 'immer'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Checkbox } from '@amzn/stencil-react-components/checkbox'
import { Input, InputWrapper, LabelPosition } from '@amzn/stencil-react-components/input'
import { Col, Hr, Row, Spacer, View } from '@amzn/stencil-react-components/layout'
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner'
import { Select } from '@amzn/stencil-react-components/select'
import { Text } from '@amzn/stencil-react-components/text'
import { ToggleSwitch } from '@amzn/stencil-react-components/toggle-switch'
import { IconBinSmall as IconBin } from '@amzn/stencil-react-icons'

import { moduleGroupRenderOption } from 'src/components/module-groups/moduleGroupRenderOption'
import { PECCreateEditValidationDTO } from 'src/models/dto/module-groups/ModuleGroupValidations'
import { FunctionalScoreDTO } from 'src/models/dto/module-groups/PECGroupDTO'
import { FunctionalScoreCreationList } from 'src/pages/module-groups/ToggleFunctionalScore'
import { ModuleGroupInformationChangeSummary } from 'src/services/module-groups/ModuleGroupEditUtils'

export interface FunctionalScoresEditProps {
    editInformationSummary: ModuleGroupInformationChangeSummary
    setEditInformationSummary: React.Dispatch<
        | ModuleGroupInformationChangeSummary
        | ((input?: ModuleGroupInformationChangeSummary) => ModuleGroupInformationChangeSummary)
    >
    functionalScores: FunctionalScoreDTO[]
    defaultModuleVersionId: string
    defaultModuleScores: string[]
    functionalScoresEnabled: boolean
    setFunctionalScoresEnabled: (isToggled: boolean) => void
    setValidationMessage: (validationMessage: string) => void
    editPECValidations?: PECCreateEditValidationDTO
    setEditPECValidations?: (
        editPECValidations:
            | PECCreateEditValidationDTO
            | ((input?: PECCreateEditValidationDTO) => PECCreateEditValidationDTO)
    ) => void
}

enableMapSet()

export const FunctionalScoresEdit = (props: FunctionalScoresEditProps) => {
    const {
        addedFunctionalScores,
        replacedScores,
        removedScores,
        defaultOrPrimaryChange: { old: originalDefaultModule },
    } = props.editInformationSummary

    const isNewDefaultModule = !!props.editInformationSummary.defaultOrPrimaryChange.new

    const getDefaultModuleScore = useCallback(
        (
            functionalScore: FunctionalScoreDTO,
            defaultModuleVersionId: string = originalDefaultModule
        ) => {
            return functionalScore.moduleVersionScoreLabelMap.get(defaultModuleVersionId) as string
        },
        [originalDefaultModule]
    )

    const deletedDefaultModuleScores = useMemo(
        () =>
            removedScores.map((functionalScoreLabel) => {
                const currentFunctionalScore = props.functionalScores.find(
                    (currFuncScore) => currFuncScore.functionalScoreLabel === functionalScoreLabel
                ) as FunctionalScoreDTO
                return getDefaultModuleScore(currentFunctionalScore, props.defaultModuleVersionId)
            }),
        [getDefaultModuleScore, props.defaultModuleVersionId, props.functionalScores, removedScores]
    )
    const existingUsedDefaultScores = useMemo(
        () =>
            props.functionalScores.map((functionalScore) =>
                getDefaultModuleScore(functionalScore, props.defaultModuleVersionId)
            ),
        [getDefaultModuleScore, props.defaultModuleVersionId, props.functionalScores]
    )
    const addedDefaultModuleScores = useMemo(
        () =>
            addedFunctionalScores.map((functionalScore) =>
                getDefaultModuleScore(functionalScore, props.defaultModuleVersionId)
            ),
        [addedFunctionalScores, getDefaultModuleScore, props.defaultModuleVersionId]
    )

    const selectedModuleScores = useMemo(() => {
        const oldReplacedScores = Array.from(replacedScores.keys())
        const newReplacedValues = Array.from(replacedScores.values())
        const remainingUsedOldDefaultScores = existingUsedDefaultScores.filter((defaultScore) => {
            return (
                !oldReplacedScores.includes(defaultScore) &&
                !deletedDefaultModuleScores.includes(defaultScore)
            )
        })

        if (isNewDefaultModule) {
            return [...addedDefaultModuleScores, ...newReplacedValues]
        } else {
            return [
                ...addedDefaultModuleScores,
                ...newReplacedValues,
                ...remainingUsedOldDefaultScores,
            ]
        }
    }, [
        replacedScores,
        addedDefaultModuleScores,
        deletedDefaultModuleScores,
        existingUsedDefaultScores,
        isNewDefaultModule,
    ])

    const addFunctionalScore = () => {
        props.setEditInformationSummary(
            produce((draft) => {
                if (!draft) return
                draft.addedFunctionalScores.push({
                    functionalScoreLabel: '',
                    moduleVersionScoreLabelMap: new Map(),
                })
            })
        )
    }

    const changeModuleScore = (index: number, moduleScore: string) => {
        props.setEditInformationSummary(
            produce((draft) => {
                if (!draft) return
                draft.addedFunctionalScores[index].moduleVersionScoreLabelMap.set(
                    props.defaultModuleVersionId,
                    moduleScore
                )
            })
        )
    }

    const updateFunctionalScoreLabel = (index: number, value: string) => {
        props.setEditInformationSummary(
            produce((draft) => {
                if (!draft) return
                draft.addedFunctionalScores[index].functionalScoreLabel = value || ''
            })
        )
    }

    const deleteFunctionalScore = (index: number) => {
        props.setEditInformationSummary(
            produce((draft) => {
                if (!draft) return
                draft.addedFunctionalScores.splice(index, 1)
            })
        )
    }

    const availableModuleScores = useMemo(
        () =>
            props.defaultModuleScores && props.defaultModuleScores.length > 0
                ? props.defaultModuleScores.filter((score) => !selectedModuleScores.includes(score))
                : [],
        [props.defaultModuleScores, selectedModuleScores]
    )

    const getFooterLabel = (functionalScoreLabel: string) => {
        return removedScores.includes(functionalScoreLabel)
            ? 'This score has been deleted'
            : undefined
    }

    const getSelectValue = (functionalScore: FunctionalScoreDTO) => {
        const defaultModuleScore = getDefaultModuleScore(functionalScore)
        const isScoreRemoved = removedScores.includes(functionalScore.functionalScoreLabel)
        return !isScoreRemoved ? replacedScores.get(defaultModuleScore) || '' : 'Deleted'
    }

    const replaceFunctionalScore = (newValue: string, oldDefaultScore: string) => {
        props.setEditInformationSummary(
            produce((draft) => {
                if (!draft) return
                draft.replacedScores.set(oldDefaultScore, newValue)
            })
        )
    }

    const handleFunctionalScoreCheckboxChange = (checked: boolean, oldDefaultScore: string) => {
        props.setEditInformationSummary(
            produce((draft) => {
                if (!draft) return
                if (checked) {
                    draft.replacedScores.delete(oldDefaultScore)
                } else {
                    draft.replacedScores.set(oldDefaultScore, '')
                }
            })
        )
    }

    const shouldDisabledCheckbox = (functionalScore: FunctionalScoreDTO) => {
        const defaultModuleScore = getDefaultModuleScore(functionalScore)
        return (
            Array.from(replacedScores.values()).includes(defaultModuleScore) ||
            addedFunctionalScores
                .map((funcScore) => getDefaultModuleScore(funcScore))
                .includes(defaultModuleScore)
        )
    }

    const removeExistingFunctionalScore = (functionalScore: FunctionalScoreDTO) => {
        const defaultModuleScore = getDefaultModuleScore(functionalScore)
        props.setEditInformationSummary(
            produce((draft) => {
                if (!draft) return
                draft.removedScores.push(functionalScore.functionalScoreLabel)
                if (draft.replacedScores.has(defaultModuleScore)) {
                    draft.replacedScores.delete(defaultModuleScore)
                }
            })
        )
    }

    const newDefaultScoreSelectIsDisabled = (functionalScore: FunctionalScoreDTO) => {
        return (
            removedScores.includes(functionalScore.functionalScoreLabel) ||
            (!isNewDefaultModule && !replacedScores.has(getDefaultModuleScore(functionalScore)))
        )
    }

    const clearOldErrorValidations = (index: number) => {
        props.setEditPECValidations?.(
            produce((draft) => {
                draft?.functionalEquivalency?.set(index, '')
            })
        )
        props.setValidationMessage('')
    }

    return (
        <Col gridGap='S400'>
            <Row gridGap='S600'>
                <Text fontSize='S200'>Functional Score?</Text>
                <ToggleSwitch
                    checked={props.functionalScoresEnabled}
                    onChange={(isToggled: boolean) => props.setFunctionalScoresEnabled(isToggled)}
                    data-test-id='functional-score-toggle'
                    disabled={props.defaultModuleScores.length === 0}
                />
            </Row>
            {props.defaultModuleScores.length === 0 && (
                <MessageBanner type={MessageBannerType.Warning}>
                    The selected Default module does not have any scores
                </MessageBanner>
            )}
            {!props.functionalScoresEnabled &&
                props.functionalScores &&
                props.functionalScores.length > 0 && (
                    <MessageBanner
                        type={MessageBannerType.Warning}
                        dataTestId='remove-func-score-warning'
                    >
                        Disabling the functional scores will delete all the score equivalencies you
                        set up in the module group once you save. Click cancel or re-enable them to
                        reset.
                    </MessageBanner>
                )}
            {props.functionalScoresEnabled && (
                <Col gridGap='S400'>
                    <ul>
                        <Col gridGap='S300'>
                            {props.functionalScores.map(
                                (functionalScore: FunctionalScoreDTO, index: number) => (
                                    <li
                                        key={`functional-${functionalScore.functionalScoreLabel}`}
                                        aria-label={`Edit functional score ${index + 1}`}
                                    >
                                        <Col gridGap='S300'>
                                            <Row gridGap='S200'>
                                                <View flex={1}>
                                                    <InputWrapper
                                                        id={`old-functional-score-wrapper-${functionalScore.functionalScoreLabel}`}
                                                        labelText={`Functional Score Name ${
                                                            index + 1
                                                        }`}
                                                        warning={
                                                            !!getFooterLabel(
                                                                functionalScore.functionalScoreLabel
                                                            )
                                                        }
                                                        footer={getFooterLabel(
                                                            functionalScore.functionalScoreLabel
                                                        )}
                                                    >
                                                        {(inputProps) => (
                                                            <Input
                                                                {...inputProps}
                                                                value={
                                                                    functionalScore.functionalScoreLabel
                                                                }
                                                                disabled
                                                            />
                                                        )}
                                                    </InputWrapper>
                                                </View>
                                                <Col>
                                                    <Spacer height='S500' />
                                                    <Button
                                                        icon={<IconBin title='Delete item' />}
                                                        isDestructive
                                                        dataTestId={`edit-score-${functionalScore.functionalScoreLabel}-delete`}
                                                        variant={ButtonVariant.Tertiary}
                                                        onClick={() => {
                                                            removeExistingFunctionalScore(
                                                                functionalScore
                                                            )
                                                            clearOldErrorValidations(index)
                                                        }}
                                                        aria-disabled={removedScores.includes(
                                                            functionalScore.functionalScoreLabel
                                                        )}
                                                    />
                                                </Col>
                                            </Row>
                                            <Row gridGap='S300' flex={1}>
                                                <View flex={1}>
                                                    <InputWrapper
                                                        id={`old-default-score-wrapper-${functionalScore.functionalScoreLabel}`}
                                                        labelText={`Current Default Module Score ${
                                                            index + 1
                                                        }`}
                                                    >
                                                        {(inputProps) => (
                                                            <Input
                                                                {...inputProps}
                                                                value={functionalScore.moduleVersionScoreLabelMap.get(
                                                                    originalDefaultModule
                                                                )}
                                                                disabled
                                                            />
                                                        )}
                                                    </InputWrapper>
                                                </View>
                                                <Col flex={1} gridGap='S100'>
                                                    <InputWrapper
                                                        id={`new-default-score-wrapper-${functionalScore.functionalScoreLabel}`}
                                                        labelText={`New Default Module Score ${
                                                            index + 1
                                                        }`}
                                                        required={
                                                            !newDefaultScoreSelectIsDisabled(
                                                                functionalScore
                                                            )
                                                        }
                                                        error={
                                                            !!props.editPECValidations?.functionalEquivalency?.get(
                                                                index
                                                            )
                                                        }
                                                        footer={
                                                            props.editPECValidations?.functionalEquivalency?.get(
                                                                index
                                                            )
                                                                ? props.editPECValidations?.functionalEquivalency?.get(
                                                                      index
                                                                  )
                                                                : undefined
                                                        }
                                                    >
                                                        {(inputProps) => (
                                                            <Select
                                                                {...inputProps}
                                                                options={[
                                                                    '',
                                                                    ...props.defaultModuleScores.filter(
                                                                        (primaryScore) =>
                                                                            !selectedModuleScores?.includes(
                                                                                primaryScore
                                                                            )
                                                                    ),
                                                                ]}
                                                                value={getSelectValue(
                                                                    functionalScore
                                                                )}
                                                                onChange={(value: string) => {
                                                                    replaceFunctionalScore(
                                                                        value,
                                                                        getDefaultModuleScore(
                                                                            functionalScore
                                                                        )
                                                                    )
                                                                    clearOldErrorValidations(index)
                                                                }}
                                                                disabled={newDefaultScoreSelectIsDisabled(
                                                                    functionalScore
                                                                )}
                                                                renderOption={
                                                                    moduleGroupRenderOption
                                                                }
                                                            />
                                                        )}
                                                    </InputWrapper>
                                                    {!isNewDefaultModule &&
                                                        !removedScores.includes(
                                                            functionalScore.functionalScoreLabel
                                                        ) && (
                                                            <InputWrapper
                                                                id={`keep-score-checkbox-${functionalScore.functionalScoreLabel}`}
                                                                labelText='Keep the same'
                                                                labelPosition={
                                                                    LabelPosition.Trailing
                                                                }
                                                            >
                                                                {(inputProps) => (
                                                                    <Checkbox
                                                                        dataTestId={`keep-score-${functionalScore.functionalScoreLabel}`}
                                                                        name={`keep-score-${functionalScore.functionalScoreLabel}`}
                                                                        checked={
                                                                            !replacedScores.has(
                                                                                getDefaultModuleScore(
                                                                                    functionalScore
                                                                                )
                                                                            )
                                                                        }
                                                                        onChange={(e) => {
                                                                            handleFunctionalScoreCheckboxChange(
                                                                                e.target.checked,
                                                                                getDefaultModuleScore(
                                                                                    functionalScore
                                                                                )
                                                                            )
                                                                            clearOldErrorValidations(
                                                                                index
                                                                            )
                                                                        }}
                                                                        {...inputProps}
                                                                        disabled={shouldDisabledCheckbox(
                                                                            functionalScore
                                                                        )}
                                                                    />
                                                                )}
                                                            </InputWrapper>
                                                        )}
                                                </Col>
                                            </Row>
                                            <Hr size='wide' />
                                        </Col>
                                    </li>
                                )
                            )}
                        </Col>
                    </ul>
                    <FunctionalScoreCreationList
                        functionalScores={addedFunctionalScores}
                        updateFunctionalScoreLabel={updateFunctionalScoreLabel}
                        availableModuleScores={availableModuleScores}
                        defaultModuleVersionId={props.defaultModuleVersionId}
                        changeModuleScore={changeModuleScore}
                        deleteFunctionalScore={deleteFunctionalScore}
                        addFunctionalScore={addFunctionalScore}
                        isAddDisabled={
                            selectedModuleScores.length === props.defaultModuleScores.length
                        }
                        isEditMode={true}
                        pecValidations={props.editPECValidations}
                        setPecValidations={props.setEditPECValidations}
                        setValidationMessage={props.setValidationMessage}
                    />
                </Col>
            )}
        </Col>
    )
}
